<template>
	<div class="access-members-editor">
		<div class="existing-items" v-if="internalValue.length">
			<div v-for="(item, idx) in internalValue" :key="idx"
				:class="['member-chip', { 'is-role': item.type === 'role' }]">
				<i :class="item.type === 'role' ? 'dx-icon-lock' : 'dx-icon-user'" />
				<span class="chip-text">{{ item.type === 'role' ? 'Role' : 'User' }}: {{ displayText(item) }}</span>
				<DxButton icon="close" styling-mode="text" @click="removeAt(idx)" height="36" />
			</div>
		</div>


		<div class="dx-info-message reduce-padding" v-else>
			<i class="dx-icon-info"></i>
			<div class="dx-info-message-text" v-if="type === 'page'">No access members yet. Add a role or a user to allow access.</div>
			<div class="dx-info-message-text" v-else-if="type === 'drive'">No access permissions set. Add a role or a user to restrict access.</div>
		</div>

		<div class="editor-row">
			<div class="mode-select">
				<label class="dx-field-label">Add</label>
				<DxSelectBox v-model="newType" :items="typeItems" display-expr="text" value-expr="value" :width="'100%'" />
			</div>

			<div class="value-input" v-if="newType === 'role'">
				<label class="dx-field-label">Role</label>
				<DxSelectBox v-model="newRole" :items="roles" placeholder="Select role…" :width="'100%'" :disabled="isLoadingRoles" />
			</div>

			<div class="value-input" v-else>
				<label class="dx-field-label">User (email)</label>
				<DxAutocomplete v-model="newUser" :data-source="userEmailOptions" :min-search-length="1" :width="'100%'" placeholder="user@example.com" :disabled="isLoadingUsers" @keydown.enter.prevent />
			</div>

			<DxButton :disabled="!canAdd" type="default" icon="plus" text="Add" :height="48" @click="onAddClick" />
		</div>

		<div class="dx-info-message" style="margin-top:8px;">
			<i class="dx-icon-info"></i>
			<div class="dx-info-message-text" v-if="type === 'page'">
				Roles and users listed here will be allowed when page access restriction is enabled. Admins are always allowed.
			</div>
			<div class="dx-info-message-text" v-else-if="type === 'drive'">
				Roles and users listed here will be allowed to access this file or folder when specified.
			</div>
		</div>
	</div>

</template>

<script setup>
import { ref, watch, computed, defineProps, defineEmits, inject, onMounted } from 'vue';
import DxButton from 'devextreme-vue/button';
import DxSelectBox from 'devextreme-vue/select-box';
import DxAutocomplete from 'devextreme-vue/autocomplete';

const $http = inject('$http');
const props = defineProps({
	type: { type: String, default: 'page' },		// 'page' or 'drive'.
	modelValue: { type: Array, default: () => [] },
});
const emit = defineEmits(['update:modelValue']);

const internalValue = ref([]);
const typeItems = [
	{ value: 'role', text: 'Role' },
	{ value: 'user', text: 'User' },
];
watch(
	() => props.modelValue,
	(val) => {
		internalValue.value = Array.isArray(val) ? [...val] : [];
	},
	{ immediate: true }
);

function update(v) {
	emit('update:modelValue', v);
}

function removeAt(idx) {
	const copy = [...internalValue.value];
	copy.splice(idx, 1);
	internalValue.value = copy;
	update(copy);
}

const newType = ref('role');
const newRole = ref('');
const newUser = ref('');

// Roles loaded from backend
const roles = ref([]);
const isLoadingRoles = ref(false);

async function loadRoles() {
	isLoadingRoles.value = true;
	try {
		const response = await $http.get('/role');
		const payload = response && response.data !== undefined ? response.data : response;
		if (Array.isArray(payload)) {
			const names = payload
				.map(r => (r && typeof r === 'object' ? String(r.name ?? '').trim() : ''))
				.filter(Boolean);
			// Deduplicate case-insensitively while preserving original casing
			const seen = new Set();
			const unique = [];
			for (const name of names) {
				const key = name.toLowerCase();
				if (seen.has(key)) continue;
				seen.add(key);
				unique.push(name);
			}
			// Sort case-insensitively but keep original casing for display
			roles.value = unique.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
		} else {
			roles.value = [];
		}
	} catch (e) {
		roles.value = [];
	} finally {
		isLoadingRoles.value = false;
	}
}

onMounted(() => {
	loadRoles();
});

// Users loaded from backend
const users = ref([]);
const isLoadingUsers = ref(false);

async function loadUsers() {
	isLoadingUsers.value = true;
	try {
		const response = await $http.get('/users');
		const payload = response && response.data !== undefined ? response.data : response;
		if (Array.isArray(payload)) {
			const normalized = [];
			const seen = new Set();
			for (const u of payload) {
				if (!u || typeof u !== 'object') continue;
				const id = u.id != null ? Number(u.id) : null;
				const email = u.email ? String(u.email).trim() : '';
				if (id == null || !email) continue;
				const key = `${id}:${email.toLowerCase()}`;
				if (seen.has(key)) continue;
				seen.add(key);
				normalized.push({ id, email });
			}
			users.value = normalized.sort((a, b) => a.email.toLowerCase().localeCompare(b.email.toLowerCase()));
		} else {
			users.value = [];
		}
	} catch (e) {
		users.value = [];
	} finally {
		isLoadingUsers.value = false;
	}
}

onMounted(() => {
	loadUsers();
});

const canAdd = computed(() => {
	if (newType.value === 'role') {
		const raw = newRole.value;
		if (!raw) return false;
		const newName = String(raw).trim().toLowerCase();
		if (!newName) return false;
		const exists = internalValue.value.some(it => it && it.type === 'role' && String(it.value ?? '').trim().toLowerCase() === newName);
		return !exists;
	}

	const rawUser = newUser.value;
	if (!rawUser) return false;
    const email = normalizeUserValue(rawUser);
    let matched = null;
    for (const u of users.value) {
        if (u && typeof u === 'object' && u.id != null && u.email && String(u.email).toLowerCase() === email) {
            matched = u;
            break;
        }
    }
    if (!matched) return false;
    const newId = Number(matched.id);
    const exists = internalValue.value.some(it => it && it.type === 'user' && it.id != null && Number(it.id) === newId);
    return !exists;
});

function normalizeUserValue(raw) {
	// Emails are unique; store lowercase for matching
	return String(raw).trim().toLowerCase();
}

function addItem() {
	if (!canAdd.value) return;
	const next = [...internalValue.value];
	if (newType.value === 'role') {
		next.push({ type: 'role', value: newRole.value });
		newRole.value = '';
	} else {
        const email = normalizeUserValue(newUser.value);
        // Resolve email to an id from provided users list (objects only); require id
        let matched = null;
        for (const u of users.value) {
            if (u && typeof u === 'object' && u.id != null && u.email && String(u.email).toLowerCase() === email) {
                matched = u;
                break;
            }
        }
        if (!matched) return; // Guard: should not happen because canAdd prevents it
        next.push({ type: 'user', id: Number(matched.id), email });
		newUser.value = '';
	}
	internalValue.value = next;
	update(next);
}

function onAddClick(dxEvent) {
	dxEvent.event.preventDefault();
	dxEvent.event.stopPropagation();
	addItem();
}

function displayText(item) {
	if (!item) return '';
	if (item.type === 'user') {
        if (item.email) return String(item.email).toLowerCase();
        // Try to resolve email from users by id
        const id = item.id != null ? Number(item.id) : null;
        if (id != null) {
            const found = users.value.find(u => typeof u === 'object' && Number(u.id) === id);
            if (found && found.email) return String(found.email).toLowerCase();
            return String(id);
        }
        return '';
	}
	return item?.value ?? '';
}

const userEmailOptions = computed(() => {
	// Accept only objects containing 'email'
	const result = [];
	for (const u of users.value) {
		if (!u || typeof u !== 'object') continue;
		const email = u.email ? String(u.email) : null;
		if (email) result.push(email);
	}
	// Deduplicate and sort for better UX
	return Array.from(new Set(result.map(e => e.toLowerCase()))).sort((a, b) => a.localeCompare(b));
});
</script>

<style lang="scss">
.access-members-editor {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 8px;

	.existing-items {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}

	.member-chip {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: var(--dx-color-bg, #f5f5f5);
		border: 1px solid #e0e0e0;
		border-radius: 16px;
		padding: 4px 8px;
	}

	.member-chip.is-role {
		border-color: var(--dx-color-primary);

		i {
			color: var(--dx-color-primary);
		}
	}

	.chip-text {
		font-size: 12px;
	}

	.editor-row {
		width: 100%;
		display: flex;
		align-items: flex-end;
		gap: 8px;
		flex-wrap: wrap;
	}

	.mode-select {
		display: flex;
		flex-direction: column;
		gap: 4px;
		min-width: 160px;
	}

	.value-input {
		display: flex;
		flex-direction: column;
		gap: 4px;
		flex: 1 1 280px;
		min-width: 240px;
	}
}
</style>